import { useState, useEffect } from "react";
import { eventApi, orgApi, eventModelApi, customerApi, financeApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { EVENTS } from "@/constants/testIds";
import { Calendar, Plus, X, Loader2, AlertCircle, Users, MapPin, ChevronRight, Network, Eye, Sparkles } from "lucide-react";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const { user } = useAuth();

  const load = () => {
    eventApi.listEvents().then(r => setEvents(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-6 animate-pulse space-y-4"><div className="h-8 bg-[#F3F3F3] rounded-lg w-64"/><div className="h-40 bg-[#F3F3F3] rounded-xl"/></div>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#181818] font-sans">Events</h1>
          <p className="text-sm text-[#706E6B] mt-1">{events.length} event(s) · {user?.tenant_name || user?.tenant_id}</p>
        </div>
        <button data-testid={EVENTS.createEventBtn} onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0176D3] text-white text-sm font-semibold hover:opacity-90 transition-opacity">
          <Plus className="w-4 h-4" /> New Event
        </button>
      </div>

      <div className="space-y-3" data-testid={EVENTS.eventList}>
        {events.map(ev => (
          <div key={ev.id} data-testid={`${EVENTS.eventCard}-${ev.event_number}`}
            onClick={() => setSelected(ev)}
            className="flex items-center justify-between p-5 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl hover:border-[#0176D3]/40 cursor-pointer card-hover transition-all">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-mono text-[#0176D3] bg-[#EEF4FF] px-2 py-0.5 rounded border border-[#0176D3]/30">{ev.event_number}</span>
                <StatusBadge status="IN_PROGRESS" />
              </div>
              <h3 className="text-base font-semibold text-[#181818] font-sans">{ev.name}</h3>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-[#706E6B]">
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{ev.event_type_name}</span>
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location_name}</span>
                {ev.guest_count && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{ev.guest_count} guests</span>}
                {ev.customer_name && <span>{ev.customer_name}</span>}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#706E6B] flex-shrink-0 ml-3" />
          </div>
        ))}
        {events.length === 0 && (
          <div className="text-center py-12 bg-[#F3F3F3] border border-dashed border-[#E5E5E5] rounded-xl">
            <Calendar className="w-8 h-8 text-[#444444] mx-auto mb-3" />
            <p className="text-sm text-[#706E6B]">No events yet. Create your first event.</p>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateEventModal onClose={() => setShowCreate(null)} onSuccess={() => { setShowCreate(false); load(); }} />
      )}

      {selected && (
        <EventDetail event={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function CreateEventModal({ onClose, onSuccess }) {
  const [eventTypes, setEventTypes] = useState([]);
  const [legalEntities, setLegalEntities] = useState([]);
  const [operatingUnits, setOperatingUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ name: "", event_type_id: "", legal_entity_id: "", operating_unit_id: "", location_id: "", customer_id: "", guest_count: "", event_date: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [apiErrors, setApiErrors] = useState([]);

  useEffect(() => {
    Promise.all([eventModelApi.listEventTypes(), orgApi.listLegalEntities(), orgApi.listOperatingUnits(), orgApi.listLocations(), customerApi.listCustomers()])
      .then(([et, le, ou, loc, cu]) => {
        setEventTypes(et.data.filter(x => x.status === "ACTIVE"));
        setLegalEntities(le.data.filter(x => x.status === "ACTIVE"));
        setOperatingUnits(ou.data.filter(x => x.status === "ACTIVE"));
        setLocations(loc.data.filter(x => x.status === "ACTIVE"));
        setCustomers(cu.data);
      }).finally(() => setLoadingData(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setApiErrors([]);
    try {
      const payload = {
        ...form,
        customer_id: form.customer_id || null,
        guest_count: form.guest_count ? parseInt(form.guest_count) : null,
        event_date: form.event_date || null,
      };
      await eventApi.createEvent(payload);
      toast.success("Event created successfully");
      onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.errors) setApiErrors(detail.errors);
      else if (detail?.blockers) setApiErrors(detail.blockers.map(b => ({ error: b.message })));
      else toast.error(detail?.message || detail || "Failed to create event");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg bg-[#F3F3F3] border border-[#E5E5E5] rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5]">
          <h3 className="text-base font-semibold text-[#181818] font-sans">Create New Event</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X className="w-4 h-4" /></button>
        </div>
        <form data-testid={EVENTS.createEventForm} onSubmit={submit} className="p-6 space-y-4">
          {loadingData ? (
            <div className="text-center py-6 text-[#706E6B] text-sm">Loading configuration...</div>
          ) : (
            <>
              <Field label="Event Name *">
                <input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Sharma Grand Wedding" required className="input" />
              </Field>
              <Field label="Event Type *">
                <select data-testid={EVENTS.eventTypeSelect} value={form.event_type_id} onChange={e=>setForm({...form,event_type_id:e.target.value})} required className="input">
                  <option value="">Select event type</option>
                  {eventTypes.map(et => <option key={et.id} value={et.id}>{et.name}</option>)}
                </select>
              </Field>
              <Field label="Legal Entity *">
                <select data-testid={EVENTS.legalEntitySelect} value={form.legal_entity_id} onChange={e=>setForm({...form,legal_entity_id:e.target.value})} required className="input">
                  <option value="">Select legal entity</option>
                  {legalEntities.map(le => <option key={le.id} value={le.id}>{le.name}</option>)}
                </select>
              </Field>
              <Field label="Operating Unit *">
                <select data-testid={EVENTS.operatingUnitSelect} value={form.operating_unit_id} onChange={e=>setForm({...form,operating_unit_id:e.target.value})} required className="input">
                  <option value="">Select operating unit</option>
                  {operatingUnits.map(ou => <option key={ou.id} value={ou.id}>{ou.name}</option>)}
                </select>
              </Field>
              <Field label="Location *">
                <select data-testid={EVENTS.locationSelect} value={form.location_id} onChange={e=>setForm({...form,location_id:e.target.value})} required className="input">
                  <option value="">Select location</option>
                  {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                </select>
              </Field>
              <Field label="Customer">
                <select value={form.customer_id} onChange={e=>setForm({...form,customer_id:e.target.value})} className="input">
                  <option value="">No customer (optional)</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Event Date">
                  <input type="date" value={form.event_date} onChange={e=>setForm({...form,event_date:e.target.value})} className="input" />
                </Field>
                <Field label="Guest Count">
                  <input type="number" value={form.guest_count} onChange={e=>setForm({...form,guest_count:e.target.value})} placeholder="500" className="input" />
                </Field>
              </div>

              {apiErrors.length > 0 && (
                <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 space-y-1">
                  {apiErrors.map((e, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-rose-400">
                      <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      {e.error || e.message}
                    </div>
                  ))}
                </div>
              )}

              <button data-testid={EVENTS.submitEventBtn} type="submit" disabled={loading}
                className="w-full py-2.5 rounded-lg bg-[#0176D3] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Create Event"}
              </button>
            </>
          )}
        </form>
        <style>{`.input { width: 100%; padding: 8px 12px; border-radius: 8px; background: #F3F3F3; border: 1px solid #E5E5E5; color: #181818; font-size: 14px; outline: none; } .input:focus { border-color: #0176D3; }`}</style>
      </div>
    </div>
  );
}

function EventDetail({ event, onClose }) {
  const navigate = useNavigate();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg bg-[#F3F3F3] border border-[#E5E5E5] rounded-2xl shadow-2xl p-6" data-testid={EVENTS.eventDetail}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-[#0176D3] bg-[#EEF4FF] px-2 py-0.5 rounded border border-[#0176D3]/30">{event.event_number}</span>
              <StatusBadge status="IN_PROGRESS" />
            </div>
            <h2 className="text-lg font-bold text-[#181818] font-sans mt-1">{event.name}</h2>
          </div>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          {[
            ["Event Type", event.event_type_name],
            ["Legal Entity", event.legal_entity_name],
            ["Operating Unit", event.operating_unit_name],
            ["Location", event.location_name],
            ["Customer", event.customer_name || "—"],
            ["Guests", event.guest_count?.toLocaleString() || "—"],
            ["Event Date", event.event_date ? new Date(event.event_date).toLocaleDateString() : "—"],
            ["Created By", event.created_by_email],
          ].map(([label, value]) => (
            <div key={label} className="flex items-center justify-between p-3 rounded-lg bg-[#F3F3F3] border border-[#EEF4FF]">
              <span className="text-xs font-mono text-[#706E6B]">{label}</span>
              <span className="text-sm text-[#181818] font-medium">{value}</span>
            </div>
          ))}
        </div>
        {event.notes && (
          <div className="mt-3 p-3 rounded-lg bg-[#F3F3F3] border border-[#EEF4FF]">
            <p className="text-xs font-mono text-[#706E6B] mb-1">Notes</p>
            <p className="text-sm text-[#444444]">{event.notes}</p>
          </div>
        )}

        {/* ── Navigation actions ──────────────────────────────────────────── */}
        <div className="mt-5 flex flex-wrap gap-2 border-t border-[#E5E5E5] pt-4">
          <button
            data-testid={`event-detail-planning-btn-${event.id}`}
            onClick={() => { onClose(); navigate(`/imagine/events/${event.id}`); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30 hover:bg-[#253550] transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" /> Event Planning
          </button>
          {event.threads_activated && (
            <>
              <button
                data-testid={`event-detail-threads-btn-${event.id}`}
                onClick={() => { onClose(); navigate(`/events/${event.id}/threads`); }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-[#EEF4FF] text-[#6832B8] border border-violet-500/30 hover:bg-violet-500/10 transition-colors"
              >
                <Network className="w-3.5 h-3.5" /> Open Threads
              </button>
              <button
                data-testid={`event-detail-360-btn-${event.id}`}
                onClick={() => { onClose(); navigate(`/events/${event.id}/360`); }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-[#EEF4FF] text-[#2E844A] border border-emerald-500/30 hover:bg-emerald-500/10 transition-colors"
              >
                <Eye className="w-3.5 h-3.5" /> Event 360
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1.5">{label}</label>
      {children}
    </div>
  );
}
